// Utils and Service
import { getErrorMessage, isCanceledError } from '../utils/helpers'

import axios from './instance'

/**
 * Gets the Dealers Billing Kpis.
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 * @param {string} filterParams - Optional filter parameters to append to the URL
 */
export const getDealersBillingKpis = async (
  setLoading,
  setError = () => {},
  setSuccess = () => {},
  filterParams = '',
) => {
  setLoading(true)

  try {
    const url = `/dealers/billing-kpis/${filterParams ? `?${filterParams}` : ''}`
    const response = await axios.get(url)
    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the Dealers Billing Metrics.
 * @param {string} url
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 * @param {AbortSignal} signal - cancels the request when a newer one supersedes it
 */
export const getDealersBillingMetrics = async (
  url,
  setLoading,
  setError = () => {},
  setSuccess = () => {},
  signal = undefined,
) => {
  setLoading(true)

  try {
    const response = await axios.get(url, { signal })
    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the individual Dealer Billing Kpis.
 * @param {string} id
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const getDealerBillingKpis = async (
  id,
  setLoading,
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(`/dealers/${id}/billing-kpis/`)
    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the Dealer Billing Cycles.
 * @param {string} dealerId
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const getDealerBillingCycles = async (
  dealerId,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(
      `/dealers/${dealerId}/billing-cycles/?expand=cleanup_details,files&order_by=-start_date&limit=99999`,
    )
    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the Dealer Outstand Billing Cycles.
 * @param {string} dealerId
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const getOutstandingDealerBillingCycles = async (
  dealerId,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(
      `/dealers/${dealerId}/billing-cycles/?order_by=start_date&limit=99999&period__is_closed=false&is_outstanding=true`,
    )
    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the Dealer Credit Memos.
 * @param {string} url
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 * @param {AbortSignal} signal - cancels the request when a newer one supersedes it
 */
export const getDealerCreditMemos = async (
  url,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
  signal = undefined,
) => {
  setLoading(true)
  try {
    const response = await axios.get(url, { signal })
    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Delete the Dealer Credit Memo.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {string} creditMemoId
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const deleteCreditMemo = async (
  dealerId,
  billingCycleId,
  creditMemoId,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    const response = await axios.delete(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/credit-memos/${creditMemoId}/`,
    )
    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Updates the billing cycle with the specified `payload`.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const updateBillingCycle = async (
  dealerId,
  billingCycleId,
  payload,
  setLoading,
  setError,
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const { data } = await axios.patch(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/`,
      payload,
    )
    setSuccess(true)
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Creates the Credit Memo.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const createCreditMemo = async (
  dealerId,
  billingCycleId,
  payload,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    const response = await axios.post(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/credit-memos/`,
      payload,
    )
    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Update the Credit Memo.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {string} creditMemoId
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const updateCreditMemo = async (
  dealerId,
  billingCycleId,
  creditMemoId,
  payload,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    const response = await axios.patch(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/credit-memos/${creditMemoId}/`,
      payload,
    )
    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Create a new file for the specified credit memo.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {string} creditMemoId
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const createCreditMemoFile = async (
  dealerId,
  billingCycleId,
  creditMemoId,
  payload,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    const response = await axios.post(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/credit-memos/${creditMemoId}/files/`,
      payload,
    )
    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    throw err
  }
}

/**
 * Create a new add back for the specified credit memo.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {string} creditMemoId
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const createCreditMemoAddBack = async (
  dealerId,
  billingCycleId,
  creditMemoId,
  payload,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    await axios.post(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/credit-memos/${creditMemoId}/add-backs/`,
      payload,
    )
    setLoading(false)
    setSuccess(true)
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    throw err
  }
}

/**
 * Create a new unbillable for the specified credit memo.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {string} creditMemoId
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const createCreditMemoUnbillable = async (
  dealerId,
  billingCycleId,
  creditMemoId,
  payload,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    await axios.post(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/credit-memos/${creditMemoId}/unbillables/`,
      payload,
    )
    setLoading(false)
    setSuccess(true)
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    throw err
  }
}

/**
 * Gets the Dealers Billing Periods.
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const getBillingPeriods = async (setError = () => {}, setSuccess = () => {}) => {
  try {
    const response = await axios.get('/billing-periods/')
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    return null
  }
}

// ------------------------------------ Exports ------------------------------------

/**
 * Creates a billing export for the period specified by `id`.
 * @param {string} id
 * @param {string} filterParams - Query string with filter parameters
 * @param {func} setError
 * @param {func} setLoading
 */
export const createBillingCycleExport = async (
  id,
  filterParams = '',
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const url = filterParams
      ? `/billing-periods/${id}/exports/?${filterParams}`
      : `/billing-periods/${id}/exports/`
    const response = await axios.post(url)

    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the billing export with the specified `exportId`.
 * @param {string} periodId
 * @param {string} exportId
 * @param {func} setError
 * @param {func} setLoading
 */
export const getBillingCycleExport = async (
  periodId,
  exportId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(`/billing-periods/${periodId}/exports/${exportId}`)

    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

// ------------------------------------ Cleanup Details ------------------------------------

/**
 * Creates a cleanup detail for the specified billing cycle.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const createCleanupDetail = async (
  dealerId,
  billingCycleId,
  payload,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    const response = await axios.post(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/cleanup-details/`,
      payload,
    )
    setLoading(false)
    setSuccess('Cleanup detail created successfully.', response.data)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Updates a cleanup detail for the specified billing cycle.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {string} cleanupDetailId
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const updateCleanupDetail = async (
  dealerId,
  billingCycleId,
  cleanupDetailId,
  payload,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    const response = await axios.patch(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/cleanup-details/${cleanupDetailId}/`,
      payload,
    )
    setLoading(false)
    setSuccess('Cleanup detail updated successfully.', response.data)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Deletes a cleanup detail for the specified billing cycle.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {string} cleanupDetailId
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const deleteCleanupDetail = async (
  dealerId,
  billingCycleId,
  cleanupDetailId,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    const response = await axios.delete(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/cleanup-details/${cleanupDetailId}/`,
    )
    setLoading(false)
    setSuccess('Cleanup detail deleted successfully.')
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Creates a billing cycle file.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {FormData} formData
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const createBillingCycleFile = async (
  dealerId,
  billingCycleId,
  formData,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    const response = await axios.post(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/files/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    setLoading(false)
    setSuccess('File uploaded successfully.', response)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Deletes a billing cycle file.
 * @param {string} dealerId
 * @param {string} billingCycleId
 * @param {string} fileId
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const deleteBillingCycleFile = async (
  dealerId,
  billingCycleId,
  fileId,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)
  try {
    const response = await axios.delete(
      `/dealers/${dealerId}/billing-cycles/${billingCycleId}/files/${fileId}/`,
    )
    setLoading(false)
    setSuccess('File deleted successfully.')
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}
